import { z } from 'zod';

/**
 * Lifecycle-sweep events — the durable record of what the retirement,
 * retro-trial and trial-ending sweeps did on a given run.
 *
 * Motivating incident (2026-08-04): both daily sweeps had been running in
 * dry-run for days and nobody could see a single thing they would have done.
 * The dry-run detail existed only as `console.log`, and two properties of the
 * worker conspired to make it unreadable:
 *
 *   1. Railway retains roughly 9.5h of worker logs.
 *   2. The sweeps are scheduled off *boot* time — `setTimeout(boot + 180s)`
 *      then every 24h — so their time of day silently moves on every redeploy.
 *
 * The scheduled review task went looking for the dry-run output, found nothing,
 * and could not distinguish "no churches were due" from "the run happened
 * twelve hours before anyone looked". That is the same shape as the WA checkout
 * incident behind ADR 0002: a silent path is indistinguishable from a path that
 * had nothing to say.
 *
 * So the sweeps emit, and they emit IDENTICALLY in dry-run and apply mode —
 * `dryRun` is a property, not a reason to stay quiet. Reading back a day of
 * `sweep.church_staged` where `terminal` is true is exactly the "what would
 * APPLY have done to whom" list, which is the question that gates the flip.
 */

/** Which sweep produced the event. */
export const SWEEP_NAMES = [
  /** Dormant never-subscribed churches: notice → reminder → final → closed. */
  'retirement',
  /** Still-building free churches: offer → reminder → walled. */
  'retro_trial',
  /** Card-on-trial pre-charge notice, a few days before the trial converts. */
  'trial_ending',
  /**
   * Google Ads offline conversions: finds paying churches whose gclid has not
   * been uploaded yet and uploads them via the Ads API (alerting a human only
   * when that path is unconfigured or failing). It mails nobody and changes no
   * account — its one write goes to Google — so it reports through the `ads.*`
   * domain and only borrows this family's failure event.
   */
  'offline_conversion',
  /**
   * Finished check-ins whose CheckForAutomations never ran: re-dispatches them.
   * Reports through the `queue.*` domain; only borrows this family's failure
   * event.
   */
  'dispatch_reconciliation',
] as const;

export type SweepName = (typeof SWEEP_NAMES)[number];

/**
 * Every stage across all three sweeps. `reminder` is deliberately shared —
 * retirement and retro-trial both have one, and the `sweep` property is what
 * disambiguates them.
 */
export const SWEEP_STAGES = [
  // retirement
  'notice',
  'reminder',
  'final',
  'closed',
  // retro-trial ('reminder' shared with retirement)
  'offer',
  'walled',
  // trial-ending
  'pre_charge_notice',
] as const;

export type SweepStage = (typeof SWEEP_STAGES)[number];

/**
 * The stages that change account state rather than just sending a warning.
 * These are the ones a human needs to eyeball before APPLY is switched on.
 */
export const TERMINAL_SWEEP_STAGES: readonly SweepStage[] = [
  'closed',
  'walled',
] as const;

export const SWEEP_FAILURE_REASONS = [
  /** APPLY was on but the Customer.io mailer was not configured — refused to run. */
  'mailer_unconfigured',
  /**
   * A sweep whose answer depends on Stripe had no `STRIPE_KEY` and refused to
   * run. Distinct from `mailer_unconfigured` because it is not about sending:
   * the offline-conversion sweep reads payments and mails nobody, and without a
   * key it would report a confident "0 uploadable" that is indistinguishable
   * from the correct answer.
   */
  'stripe_unconfigured',
  /** The sweep threw. Without this, a crashing sweep looks like a quiet one. */
  'threw',
] as const;

export type SweepFailureReason = (typeof SWEEP_FAILURE_REASONS)[number];

const sweep = z.enum(SWEEP_NAMES);

/**
 * `false` only when the sweep actually sent mail and wrote to the DB. Every
 * event carries it so a dry-run run and a live run are never confused for one
 * another after the fact.
 */
const dryRun = z.boolean();

export const sweepsSchemas = {
  /**
   * One per church that was due a stage on this run — emitted whether or not
   * the stage was actually delivered, because in dry-run nothing is. This is
   * the event that names names.
   */
  'sweep.church_staged': z.object({
    churchId: z.string(),
    churchName: z.string(),
    dryRun,
    /** Admin recipients the stage would go to. 0 means it was skipped. */
    recipients: z.number().int().nonnegative(),
    stage: z.enum(SWEEP_STAGES),
    sweep,
    /**
     * True for `closed` / `walled` — the stage that changes what the church can
     * do. Filter on this to get the eyeball list.
     */
    terminal: z.boolean(),
    /** Retirement only: the `standard` (60d) or `empty` (14d) track. */
    track: z.enum(['standard', 'empty']).optional(),
  }),

  /**
   * A sweep did not complete. Both branches that used to end in a bare
   * `return` or a `console.error` now land here (ADR 0002: instrument the
   * failure branches, not just the happy path).
   */
  'sweep.run_failed': z.object({
    dryRun,
    /** Error message when `reason` is `threw`. */
    message: z.string().optional(),
    reason: z.enum(SWEEP_FAILURE_REASONS),
    sweep,
  }),

  /**
   * One per sweep run. The counters mirror each sweep's own result object; the
   * optional ones are the sweep-specific counters that only one sweep produces.
   */
  'sweep.run_completed': z.object({
    /** Churches whose account state changed (or would have): closed + walled. */
    accountsChanged: z.number().int().nonnegative(),
    /** Trial-ending: already warned on an earlier run, so skipped. */
    alreadyWarned: z.number().int().nonnegative().optional(),
    /**
     * Retirement: closures whose Slack alert was withheld by
     * `RETIREMENT_CLOSURE_ALERTS=false`. The close still happened and the
     * ladder still moved; only campaign 27's per-church Slack post was
     * skipped.
     *
     * Set for the initial APPLY drain, which closes tens of churches a day for
     * about a week and would post one Slack message for each — Customer.io
     * cannot digest them, since N closures are N events on N different
     * profiles and every throttle it offers is per-person. Campaign 28 flooded
     * the channel exactly this way on 2026-09-01 and was stopped the same day.
     *
     * Reported so a flag left set after the drain surfaces in the evening sweep
     * review instead of quietly restoring the invisibility campaign 27 was
     * built to end.
     */
    closureAlertsSuppressed: z.number().int().nonnegative().optional(),
    /**
     * Retirement: closes held because Customer.io showed no delivered earlier
     * warning for the church (or the check could not finish). Accepted is not
     * delivered; the church stays open and is due again next run.
     */
    closesHeldUnconfirmed: z.number().int().nonnegative().optional(),
    dryRun,
    /**
     * Churches this sweep owns and acted on. For trial-ending this is the
     * count of trials inside the lead window.
     */
    eligible: z.number().int().nonnegative(),
    emailsSent: z.number().int().nonnegative(),
    /** Retirement: dormant churches that never built anything (14-day track). */
    emptyTrack: z.number().int().nonnegative().optional(),
    /**
     * Retirement: churches in neither sweep's window — too quiet for the
     * retro-trial, not yet dormant enough for retirement. Nothing contacts
     * them. This was invisible for 97 churches once; never again.
     */
    holding: z.number().int().nonnegative().optional(),
    /** Retirement: admins moved onto the `closed` ladder rung by this run. */
    ladderClosed: z.number().int().nonnegative().optional(),
    /**
     * Retirement: admins left on their existing rung because they still run a
     * live church — `closed` is lowest-precedence, so writing it would demote.
     */
    ladderSkippedStillLive: z.number().int().nonnegative().optional(),
    scanned: z.number().int().nonnegative(),
    /** Retirement: in-flight sequences cancelled because a church came back. */
    sequencesCancelled: z.number().int().nonnegative().optional(),
    skippedInternal: z.number().int().nonnegative(),
    skippedNoAdminEmail: z.number().int().nonnegative(),
    /** Trial-ending: a Stripe customer with no matching church row. */
    skippedNoChurch: z.number().int().nonnegative().optional(),
    /**
     * Retro-trial: duplicate signups excluded because an admin already pays for
     * a DIFFERENT church. `customer: null` describes the row, not the person —
     * on 2026-08-10 that gap mailed "your free access ends" to a paying church
     * of four years and had it booked for a wall. Counted, never filtered away
     * in SQL, so the band stays visible the way `holding` had to be.
     */
    skippedStripeLinked: z.number().int().nonnegative().optional(),
    sweep,
    /**
     * Retirement + retro-trial: churches that were DUE a stage but deferred
     * because the run's send budget (`RETIREMENT_SWEEP_LIMIT` /
     * `RETRO_TRIAL_LIMIT`) was spent. Untouched — no claim, no
     * `sweep.church_staged` — so they are simply due again next run.
     *
     * Without this the throttle is a silent cap. On 2026-08-10, the first
     * retro-trial APPLY run mailed 30 of the 113 emails its dry-run twin had
     * wanted, and the ~74 deferred churches were only recoverable by diffing
     * against that twin. Once APPLY is the only mode there is no twin to diff,
     * and `eligible: 101, emailsSent: 30` reads as a bug rather than a budget.
     */
    throttled: z.number().int().nonnegative().optional(),
    /** Retro-trial: offered, then went quiet — dropped rather than walled. */
    wentQuiet: z.number().int().nonnegative().optional(),
  }),
} as const;
